import { TableRow, TableCell } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { flexRender, Row } from '@tanstack/react-table';
import useMapPermissions from '../../../../hooks/useMapPermissions';
import type { FileItem } from '../../../../types/File/File.types';
import { hasAdminRole } from '../../../../utils/utils';
import { FileActions } from '../FileActions/FileActions';

interface CustomTableRowProps {
    readonly row: Row<FileItem>;
    readonly file: FileItem;
    readonly dropTargets: Record<string, boolean>;
    readonly selectedItems: string[];
    readonly onRename: (item: FileItem) => void;
    readonly onDelete: (item: FileItem) => void;
    readonly onMoveToFolder: (file: FileItem) => void;
    readonly onDownload: (file: FileItem) => void;
    readonly handleDragStart: (e: React.DragEvent, file: FileItem) => void;
    readonly handleDragEnd: () => void;
    readonly handleDragOver: (e: React.DragEvent, file: FileItem) => void;
    readonly handleDragLeave: (e: React.DragEvent, folderId: string) => void;
    readonly handleDrop: (e: React.DragEvent, file: FileItem) => void;
    readonly handleRowClick: (e: React.MouseEvent, file: FileItem) => void;
    readonly onPrefetchPermissions: (fileId: string) => void;
    readonly userInfo?: IAuthMeV2OutputDto;
    readonly onReplace: (file: FileItem) => void;
}

export function TableRowPermissions({
    row,
    file,
    dropTargets,
    selectedItems,
    onRename,
    onDelete,
    onMoveToFolder,
    onDownload,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRowClick,
    onPrefetchPermissions,
    userInfo,
    onReplace,
}: CustomTableRowProps) {
    const isMindyAdmin = hasAdminRole(userInfo?.roles);
    const { isAdmin, isEditor } = useMapPermissions(file);
    const shouldShowActions = isAdmin || isEditor || isMindyAdmin;

    return (
        <TableRow
            key={row.id}
            data-testid={`select-row-${file.id}`}
            data-state={row.getIsSelected() && 'selected'}
            className={`${
                file.type === 'folder' && dropTargets[file.id]
                    ? 'bg-primary/10 dark:bg-primary/20 border-primary'
                    : 'hover:bg-primary/10 dark:hover:bg-primary/20'
            } ${selectedItems.includes(file.id) ? 'bg-primary/5 dark:bg-primary/10' : ''} cursor-pointer`}
            draggable={file.type === 'file'}
            onDragStart={(e) => handleDragStart(e, file)}
            onDragEnd={handleDragEnd}
            onDragOver={file.type === 'folder' ? (e) => handleDragOver(e, file) : undefined}
            onDragLeave={file.type === 'folder' ? (e) => handleDragLeave(e, file.id) : undefined}
            onDrop={file.type === 'folder' ? (e) => handleDrop(e, file) : undefined}
            onClick={(e) => handleRowClick(e, file)}
            onMouseEnter={() => {
                // Solo hacer prefetch para archivos (no carpetas)
                if (file.type === 'file') {
                    onPrefetchPermissions(file.id);
                }
            }}
        >
            {row.getVisibleCells().map((cell) => {
                if (cell.column.id === 'actions') {
                    return (
                        <TableCell key={cell.id}>
                            <button
                                className="flex justify-end items-center actions-container"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`actions-${file.id}`}
                            >
                                {shouldShowActions && (
                                    <FileActions
                                        file={file}
                                        onRename={onRename}
                                        onDelete={onDelete}
                                        onMoveToFolder={onMoveToFolder}
                                        onDownload={onDownload}
                                        isMindyAdmin={isMindyAdmin}
                                        onReplace={onReplace}
                                    />
                                )}
                            </button>
                        </TableCell>
                    );
                }

                return <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>;
            })}
        </TableRow>
    );
}
